/**
 * Labstep
 *
 * @module core/Select/Search/Modal/Content
 * @desc Modal content for search select
 */

import EntityActionCreate from 'labstep-web/components/Entity/Action/Create';
import EntitySearch from 'labstep-web/components/Entity/Search';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import type { ParamsStateContextType } from 'labstep-web/contexts/params-state/types';
import { Loadable } from 'labstep-web/core/Loadable';
import { SearchSelectModalEmptyState } from 'labstep-web/core/Select/Search/Modal/EmptyState';
import { SearchSelectModalEmptyStateSearch } from 'labstep-web/core/Select/Search/Modal/EmptyState/Search';
import { GridSearchModal } from 'labstep-web/grid/SearchModal';
import type React from 'react';
import { useMemo } from 'react';
import type { ISearchSelectModalContentProps } from './types';
import { getBody, getCreateOptions } from './utils';

export const SearchSelectModalContent: React.FC<
  ISearchSelectModalContentProps
> = ({
  entityName,
  creatable,
  createBody,
  createAction,
  searchProps,
  columnDefs,
  toggleModal,
  rowSelection = 'single',
  onChange,
  onSelectionChanged,
}) => {
  const options = useMemo(() => {
    const onChangeToggle = (option: any): void => {
      onChange(option);
      toggleModal();
    };
    return getCreateOptions(entityName, onChangeToggle);
  }, [entityName, onChange, toggleModal]);

  const paramsStateContext: ParamsStateContextType = {
    name: 'search_modal',
    initialSearchParams: searchProps?.initialSearchParams,
  };

  return (
    <ParamsStateContextProvider value={paramsStateContext}>
      <EntitySearch
        searchBarProps={{
          autoFocus: true,
        }}
        action={
          creatable
            ? createAction ||
              (({
                searchParams,
              }: {
                searchParams: Record<string, unknown>;
              }) => (
                <EntityActionCreate
                  entityName={entityName}
                  body={getBody(createBody, searchParams)}
                  options={options as any}
                  actionComponentProps={{
                    type: 'button',
                  }}
                />
              ))
            : undefined
        }
        entityName={entityName}
        noResultsMessageOnSearch={({ searchParams }) => (
          <SearchSelectModalEmptyStateSearch
            entityName={entityName}
            body={getBody(createBody, searchParams)}
            options={options as any}
            name={searchParams?.search_query as string}
            creatable={creatable}
          />
        )}
        noResultsMessage={
          <SearchSelectModalEmptyState
            entityName={entityName}
            body={getBody(createBody)}
            options={options as any}
            creatable={creatable}
            action={createAction}
          />
        }
        useAllPages
        {...searchProps}
      >
        {(props) => (
          <Loadable>
            <GridSearchModal
              rowSelection={rowSelection}
              columnDefs={columnDefs}
              onSelectionChanged={onSelectionChanged}
              {...props}
            />
          </Loadable>
        )}
      </EntitySearch>
    </ParamsStateContextProvider>
  );
};
